// src/components/PostCarousel.js
//
// IG paritesi — çoklu medyalı gönderi (carousel) görüntüleyici.
// Scroll-snap yatay kaydırma (mobilde parmakla), desktop'ta ok butonları,
// altta nokta göstergesi, sağ üstte "1/3" sayacı.
//
// YÜKSEKLİK KURALI (IG'nin gerçek davranışı): kapsayıcı, İLK medyanın
// doğal oranına kilitlenir — oran 1.91:1 (yatay sınır) ile 4:5 (dikey
// sınır) aralığına sıkıştırılır. Medyalar ASLA kırpılmaz (object-fit:
// contain); ilk slayt tam oturur, farklı oranlı diğer slaytlar ince
// siyah bantla sığar. Oran ilk medyanın onLoad/onCanPlay'inde ölçülür.
//
// Tek medyalı gönderiler bu bileşene girmez (Post/PostMobile koşullu
// render eder) — eski davranış birebir korunur.

import React, { useRef, useState, useCallback } from "react";
import FeedVideo from "./FeedVideo";
import { resolveVideoUrl } from "../utils/videoSource";
import "./PostCarousel.css";

// IG oran sınırları (yükseklik/genişlik)
const MAX_RATIO = 4 / 3;      // 3:4 — dikey tavan (IG 2025: 1080×1440)
const MIN_RATIO = 1 / 1.91;   // 1.91:1 — yatay taban

interface CarouselItem {
  type?: string;
  url?: string;
  w?: number;
  h?: number;
  [key: string]: any;
}

interface Props {
  items: CarouselItem[];
  altText?: string;
  onMediaClick?: (e: React.MouseEvent) => void;
  onFirstLoad?: (e: any) => void;
}

export default function PostCarousel({ items, altText, onMediaClick, onFirstLoad }: Props) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [idx, setIdx] = useState(0);
  // İlk medyanın doc'taki w/h'ı varsa CLS'siz başla; yoksa kare → ölçüm
  const first = items[0] || {};
  // `!= null` eklendi (davranış deltası YOK): `undefined > 0` ve `null > 0`
  // zaten `false` üretiyordu — kontrol yalnız daraltmayı derleyiciye gösteriyor.
  const declared = first.w != null && first.h != null && first.w > 0 && first.h > 0
    ? Math.min(MAX_RATIO, Math.max(MIN_RATIO, first.h / first.w))
    : null;
  const [ratio, setRatio] = useState(declared || 1);
  const count = items.length;

  const measureFirst = useCallback((e: any) => {
    const t = e?.target;
    const w = t?.naturalWidth || t?.videoWidth || 0;
    const h = t?.naturalHeight || t?.videoHeight || 0;
    if (w > 0 && h > 0) {
      setRatio(Math.min(MAX_RATIO, Math.max(MIN_RATIO, h / w)));
    }
  }, []);

  const handleFirstLoad = useCallback((e: any) => {
    measureFirst(e);
    onFirstLoad?.(e);
  }, [measureFirst, onFirstLoad]);

  const handleScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    setIdx(Math.max(0, Math.min(count - 1, i)));
  }, [count]);

  const goTo = (i: number) => {
    const el = trackRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(count - 1, i));
    el.scrollTo({ left: clamped * el.clientWidth, behavior: "smooth" });
  };

  return (
    <div className="post-carousel">
      <div
        className="post-carousel-track"
        ref={trackRef}
        onScroll={handleScroll}
        style={{ aspectRatio: `1 / ${ratio}` }}
      >
        {items.map((it, i) => (
          <div className="post-carousel-slide" key={i}>
            {it.type === "video" ? (
              <FeedVideo
                /* PR-39 — R2/CDN-ready per-item: it.cdnUrl varsa CDN, yoksa it.url. */
                src={resolveVideoUrl(it) || it.url}
                poster={it.poster}
                onCanPlay={i === 0 ? handleFirstLoad : undefined}
                onClick={onMediaClick}
              />
            ) : (
              <img
                src={it.url}
                alt={altText || `Gönderi görseli ${i + 1}/${count}`}
                loading={i === 0 ? "eager" : "lazy"}
                decoding="async"
                draggable="false"
                onLoad={i === 0 ? handleFirstLoad : undefined}
                onClick={onMediaClick}
              />
            )}
          </div>
        ))}
      </div>

      <span className="post-carousel-counter">{idx + 1}/{count}</span>

      {idx > 0 && (
        <button
          type="button"
          className="post-carousel-arrow prev pressable"
          onClick={() => goTo(idx - 1)}
          aria-label="Önceki medya"
        >
          ‹
        </button>
      )}
      {idx < count - 1 && (
        <button
          type="button"
          className="post-carousel-arrow next pressable"
          onClick={() => goTo(idx + 1)}
          aria-label="Sonraki medya"
        >
          ›
        </button>
      )}

      <DotIndicator count={count} idx={idx} />
    </div>
  );
}

// IG-tarzı nokta göstergesi. <=7 medyada düz sıra (eski davranış birebir).
// 8+ medyada KAYAN PENCERE: aktif nokta ortada, pencere kenarındaki noktalar
// küçülerek "devamı var" hissi verir (transform:scale → layout adımı sabit,
// bu yüzden translate matematiği tutar). Salt görsel; kaydırma/snap'e dokunmaz.
function DotIndicator({ count, idx }: { count: number; idx: number }) {
  const WINDOW = 7;
  const STEP = 10; // 6px nokta + 4px gap (PostCarousel.css ile aynı)
  const windowed = count > WINDOW;

  if (!windowed) {
    return (
      <div className="post-carousel-dots" aria-hidden="true">
        {Array.from({ length: count }).map((_, i) => (
          <span key={i} className={`post-carousel-dot ${i === idx ? "active" : ""}`} />
        ))}
      </div>
    );
  }

  const start = Math.max(0, Math.min(idx - 3, count - WINDOW));
  const atStart = start === 0;
  const atEnd = start === count - WINDOW;
  const sizeClass = (i: number) => {
    if (i < start || i > start + WINDOW - 1) return "edge-hidden";
    const pos = i - start;
    if ((pos === 0 && !atStart) || (pos === WINDOW - 1 && !atEnd)) return "edge-tiny";
    if ((pos === 1 && !atStart) || (pos === WINDOW - 2 && !atEnd)) return "edge-small";
    return "";
  };

  return (
    <div className="post-carousel-dots post-carousel-dots--win" aria-hidden="true">
      <div className="post-carousel-dots-viewport">
        <div
          className="post-carousel-dots-track"
          style={{ transform: `translateX(${-start * STEP}px)` }}
        >
          {Array.from({ length: count }).map((_, i) => (
            <span
              key={i}
              className={`post-carousel-dot ${i === idx ? "active" : ""} ${sizeClass(i)}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
